import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CmsService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Fetches the CMS page list.
 * @param {Object} params - Query parameters (page, limit, search_text)
 */
const useListCms = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["cms-list", params],
    queryFn: () => CmsService.getList(params),
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: Object.keys(params).length > 0,
  });
};

/**
 * Fetches a single CMS page by UUID.
 * @param {string} uuid - CMS page UUID
 */
const useGetCms = (uuid, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["cms", uuid],
    queryFn: () => CmsService.get(uuid),
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: !!uuid,
  });
};

/**
 * Fetches a CMS page's content for the public /cms/:slug page (no login).
 * No retries: a 404 (unknown/inactive page) should show "not found" straight away.
 * @param {string} slug - CMS page slug
 * @param {string} [languageCode] - Language code
 */
const usePublicCmsPage = (slug, languageCode) => {
  return useQuery({
    queryKey: ["cms-public", slug, languageCode],
    queryFn: () => CmsService.getPublicPage(slug, languageCode),
    retry: false,
    refetchOnWindowFocus: false,
    // Always show what the admin last saved
    staleTime: 0,
    enabled: !!slug,
  });
};

/**
 * Adds a new CMS page.
 * @param {function} onSuccess - Callback to run if the add request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the add request fails.
 */
const useAddCms = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (data) => CmsService.add(data),
    onSuccess,
    onError,
  });
};

/**
 * Updates a CMS page.
 * @param {function} onSuccess - Callback to run if the update request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the update request fails.
 */
const useUpdateCms = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (data) => CmsService.update(data),
    onSuccess,
    onError,
  });
};

/**
 * Activates/deactivates a CMS page. Mutation variables: { uuid, isActive }.
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 */
const useUpdateCmsStatus = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: ({ uuid, isActive }) => CmsService.updateStatus(uuid, isActive),
    onSuccess,
    onError,
  });
};

/**
 * Deletes a CMS page by UUID.
 * @param {function} onSuccess - Callback to run if the delete request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the delete request fails.
 */
const useDeleteCms = (onSuccess, onError = defaultError) => {
  return useMutation({
    mutationFn: (uuid) => CmsService.delete(uuid),
    onSuccess,
    onError,
  });
};

export { useListCms, useGetCms, usePublicCmsPage, useAddCms, useUpdateCms, useUpdateCmsStatus, useDeleteCms };
